/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowData,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {useState, FC, useEffect} from 'react'
import {Row, Col, Pagination, Form, Spinner, Button} from 'react-bootstrap'
import {Grouping} from '../core/_models'
import {Link} from 'react-router-dom'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../../modules/auth/core/AbilityContext'
import {deleteGrouping} from '../core/_requests'
import useAxiosPrivate from '../../../../libs/useAxiosPrivate'
import {getISO2ByISO3} from '../../../../libs/misc'
import {showSwal} from '../../../../../ui-kit'

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClass: string
    cellClass: string
  }
}

type ListGroupingsTableProps = {
  rows?: Grouping[] | null
  isLoading: boolean
  onGroupingDelete: (deletedId: number) => void
  subject: string
}

const confirmDialog = (title: string) => {
  return showSwal({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
  })
}

const ListGroupingsTable: FC<ListGroupingsTableProps> = ({
  rows,
  isLoading,
  onGroupingDelete,
  subject,
}) => {
  const axiosPrivate = useAxiosPrivate()
  const [internalLoading, setInternalLoading] = useState(false)
  const [listGroupings, setListGroupings] = useState<Grouping[]>([])

  const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 10})
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')

  const ability = useAbility(AbilityContext)

  const columnHelper = createColumnHelper<Grouping>()

  const columns = [
    columnHelper.accessor('grp', {
      header: 'Group',
      cell: (info) => info.getValue(),
      meta: {
        headerClass: 'ps-4 text-start w-100px',
        cellClass: 'text-start w-100px',
      },
    }),
    columnHelper.accessor('title', {
      header: 'Title',
      cell: (info) => info.getValue(),
      meta: {
        headerClass: 'text-start w-250px',
        cellClass: 'text-start w-250px',
      },
    }),
    columnHelper.accessor('economies', {
      header: 'Economies',
      cell: (info) => {
        if (!info.getValue()) return <span>-</span>
        const countries = info.getValue().split(';')
        return (
          <div className='countries'>
            {countries.map((rawCountry: string, idx: number) => {
              const country = rawCountry.trim()
              let iso2 = getISO2ByISO3(country)
              if (country === 'EUN') iso2 = 'EU'

              // 🔑 Explicit EUN handling OR fallback
              return country === 'TWN' || !iso2 ? (
                <span key={idx} className='badge text-white bg-dark me-1' title={country}>
                  {country}
                </span>
              ) : (
                <span key={idx} className={`fi fi-${iso2.toLowerCase()} me-2`} title={country} />
              )
            })}
          </div>
        )
      },
      meta: {
        headerClass: 'text-start w-320px',
        cellClass: 'text-start w-320px',
      },
    }),
    columnHelper.accessor('customs_union', {
      header: 'Customs Union',
      cell: (info) => (info.getValue() === 1 ? 'Yes' : 'No'),
      meta: {
        headerClass: 'text-center w-120px',
        cellClass: 'text-center w-120px',
      },
    }),
    columnHelper.accessor('diplay_multiple', {
      header: 'Display Multiple',
      cell: (info) => (info.getValue() === 1 ? 'Yes' : 'No'),
      meta: {
        headerClass: 'text-center w-120px',
        cellClass: 'text-center w-120px',
      },
    }),
    (ability.can('update', subject) || ability.can('delete', subject)) &&
      columnHelper.accessor('id', {
        header: 'Actions',
        cell: (info) => {
          const id = info.getValue()
          return (
            <div className='d-flex gap-3 justify-content-center'>
              {ability.can('update', subject) && id ? (
                <Link to={`/groupings/${id}`} className='link-primary'>
                  <span className='fa fa-eye'></span>
                </Link>
              ) : (
                ''
              )}
              {ability.can('delete', subject) && id ? (
                <Link to='#' className='link-danger' onClick={() => handleDelete(id)}>
                  <span className='fa fa-trash'></span>
                </Link>
              ) : (
                ''
              )}
            </div>
          )
        },
        meta: {
          headerClass: 'text-center w-100px',
          cellClass: 'text-center w-100px',
        },
        enableSorting: false,
      }),
  ].filter(Boolean) as ColumnDef<Grouping, any>[]

  const handleDelete = (id: number) => {
    confirmDialog('Are you sure you want to delete this Grouping?').then((result) => {
      if (!result.isConfirmed) return

      deleteGrouping(axiosPrivate, id).then((res) => {
        if (res.status === 200) {
          showSwal({
            title: 'Grouping deleted',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            onGroupingDelete(id)
          })
        }
      })
    })
  }

  useEffect(() => {
    setListGroupings([])
    setInternalLoading(true)
    return () => {
      setListGroupings([])
      setInternalLoading(false)
    }
  }, [])

  useEffect(() => {
    setInternalLoading(isLoading)
  }, [isLoading])

  useEffect(() => {
    if (rows) {
      setListGroupings(rows)
    }
    setInternalLoading(false)
  }, [rows])

  const dataTable = useReactTable({
    data: listGroupings || [],
    columns,
    state: {
      pagination,
      sorting,
      globalFilter,
    },
    pageCount: Math.ceil((listGroupings?.length || 0) / pagination.pageSize),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  function paginationRange() {
    const currentPage = pagination.pageIndex + 1
    const totalPages = dataTable.getPageCount()
    const maxPagesToShow = 3
    const halfRange = Math.floor(maxPagesToShow / 2)

    let startPage = Math.max(1, currentPage - halfRange)
    let endPage = Math.min(totalPages, currentPage + halfRange)

    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1)
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)
      }
    }

    const range: any = []

    if (startPage > 1) {
      range.push(1)
      if (startPage > 2) {
        range.push('...')
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      range.push(i)
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...')
      }
      range.push(totalPages)
    }

    return range
  }

  const handleNavigationPageChange = (nav) => {
    setPagination({
      ...pagination,
      pageIndex: nav === 'prev' ? Math.max(0, pagination.pageIndex - 1) : pagination.pageIndex + 1,
    })
  }

  const handleDisplayPageChange = (e) => {
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value,
    })
  }

  const pages = paginationRange()
  const firstIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize + 1

  const lastIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize +
    dataTable.getRowModel().rows.length

  const displayHeader = dataTable.getHeaderGroups().map((headerGroup) => (
    <tr className='fw-normal py-4 align-middle' key={headerGroup.id}>
      {headerGroup.headers.map((header) => {
        return (
          <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
            <div
              className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
              title={
                header.column.getCanSort()
                  ? header.column.getNextSortingOrder() === 'asc'
                    ? 'Sort ascending'
                    : header.column.getNextSortingOrder() === 'desc'
                    ? 'Sort descending'
                    : 'Reset'
                  : undefined
              }
              onClick={header.column.getToggleSortingHandler()}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
              {header.column.getCanSort() && (
                <span className='ms-2'>
                  {{
                    asc: <i className='fa-solid fa-sort-up'></i>,
                    desc: <i className='fa-solid fa-sort-down'></i>,
                    false: <i className='fa-solid fa-sort'></i>,
                  }[header.column.getIsSorted() as string] ?? <i className='fa-solid fa-sort'></i>}
                </span>
              )}
            </div>
          </th>
        )
      })}
    </tr>
  ))

  const displayBody =
    dataTable.getRowModel().rows.length > 0 ? (
      dataTable.getRowModel().rows.map((row) => (
        <tr key={row.id} className='align-middle'>
          {row.getVisibleCells().map((cell) => {
            return (
              <td key={cell.id} className={cell.column.columnDef.meta?.cellClass}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            )
          })}
        </tr>
      ))
    ) : (
      <tr className='align-middle'>
        <td colSpan={99} className='text-center'>
          No Data
        </td>
      </tr>
    )

  return (
    <div style={{minHeight: '400px', position: 'relative'}}>
      {!internalLoading ? (
        <>
          <Row className='mb-5 justify-content-between'>
            <Col lg={5} md={5} className='d-flex justify-content-start align-items-center'>
              <i className='fa-solid fa-circle text-success fs-9 me-2'></i>
              Showing {dataTable.getRowModel().rows.length > 0 ? firstIndexPerPages : 0} to{' '}
              {dataTable.getRowModel().rows.length > 0 ? lastIndexPerPages : 0} of{' '}
              {listGroupings?.length || 0} results
            </Col>
            <Col lg={3} md={3} className='action-table'>
              <Form.Group className='form-group' controlId='search-groupings'>
                <div className='input-with-icon'>
                  <Form.Control
                    type='text'
                    className='form-control-md'
                    onChange={(e) => {
                      setPagination({
                        pageIndex: 0,
                        pageSize: 10,
                      })
                      setGlobalFilter(e.target.value)
                    }}
                    placeholder='Search by Group or Title'
                    value={globalFilter}
                  />
                  <div className='input-icon'>
                    <i className='fa-solid fa-magnifying-glass fa-rotate-90 text-primary'></i>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>
          <table
            className='gy-3 table table-striped table-bordered table-hover table-header-sticky'
            id='list_groupings'
          >
            <thead className='table-dark'>{displayHeader}</thead>
            <tbody className='gy-1 overflow-auto'>{displayBody}</tbody>
          </table>
          <Row className='my-3'>
            <Col md={6} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size='sm'
                className='w-80px mx-2'
                onChange={handleDisplayPageChange}
                value={pagination.pageSize}
              >
                <option value='10'>10</option>
                <option value='20'>20</option>
                <option value='50'>50</option>
                <option value='100'>100</option>
              </Form.Select>
              list groupings
            </Col>
            <Col md={6} className='d-flex justify-content-end'>
              <Pagination>
                <Pagination.Prev
                  onClick={() => handleNavigationPageChange('prev')}
                  disabled={pagination.pageIndex === 0}
                />
                {pages.map((page, index) => {
                  if (page === '...') {
                    return <Pagination.Ellipsis key={index} />
                  }
                  return (
                    <Pagination.Item
                      key={index}
                      active={page === pagination.pageIndex + 1}
                      onClick={() => {
                        setPagination({
                          ...pagination,
                          pageIndex: page - 1,
                        })
                      }}
                    >
                      {page}
                    </Pagination.Item>
                  )
                })}
                <Pagination.Next
                  onClick={() => handleNavigationPageChange('next')}
                  disabled={pagination.pageIndex >= dataTable.getPageCount() - 1}
                />
              </Pagination>
            </Col>
          </Row>
        </>
      ) : (
        <div className='d-flex align-items-center justify-content-center' style={{height: '50vh'}}>
          <Spinner animation='border' role='status' variant='primary' className='me-3' />
          <div className='loading-text'>
            Loading
            <span className='dot'>.</span>
            <span className='dot'>.</span>
            <span className='dot'>.</span>
          </div>
        </div>
      )}
    </div>
  )
}

export {ListGroupingsTable}
